import type { SubmitCampaignForReviewData } from '@jobilla/nexus-sdk'
import { atom, useAtom } from 'jotai'
import { useCallback } from 'react'
import { CustomFields } from '../../views/settings/sub-pages/custom-fields/CustomFields'
import { CampaignChannel } from '../../views/shared/conformed/review'

export type ReviewCompleteData = {
	feedAd?: Blob
	storyAd?: Blob
	budget: number // euros
	startDate: Date
	endDate: Date
	custom_fields: CustomFields
	predictedBudget?: number | null // euros
	channels: CampaignChannel[]
	paymentMethods: SubmitCampaignForReviewData['paymentMethods']
	/** Saved card to authorise, when the user pays by card. Undefined for wallet/invoice/no-payment. */
	stripePaymentMethodId?: string
	/** Billing profile to bill, when the org has more than one. Null bills the primary customer. */
	profileId?: string | null
	inventoryItems?: SubmitCampaignForReviewData['inventoryItems']
}

// Atoms to store the complete callbacks
export const onReviewCompleteAtom = atom<((data: ReviewCompleteData) => Promise<void>) | undefined>(
	undefined,
)
export const onRedirectAfterCompleteAtom = atom<(() => void) | undefined>(undefined)

export function useCompleteEditor() {
	const [onReviewComplete, setOnReviewCompleteRaw] = useAtom(onReviewCompleteAtom)
	const [onRedirectAfterComplete, setOnRedirectAfterCompleteRaw] = useAtom(onRedirectAfterCompleteAtom)

	// Wrap the setters to avoid double-callback confusion
	// Jotai's setter treats a single function as a functional update, so we wrap it here
	// These are memoized to prevent infinite loops when used as effect dependencies
	const setOnReviewComplete = useCallback((fn: (data: ReviewCompleteData) => Promise<void>) => {
		setOnReviewCompleteRaw(() => fn)
	}, [setOnReviewCompleteRaw])

	const setOnRedirectAfterComplete = useCallback((fn: () => void) => {
		setOnRedirectAfterCompleteRaw(() => fn)
	}, [setOnRedirectAfterCompleteRaw])

	return {
		onReviewComplete,
		setOnReviewComplete,
		onRedirectAfterComplete,
		setOnRedirectAfterComplete,
	}
}
